import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner, Badge } from 'react-bootstrap';
import { FaUserShield, FaCheck, FaTimes } from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import permisosApi from '../../../services/api/permisosApi';
import 'react-toastify/dist/ReactToastify.css';

const PermisosRoles = () => {
  const [roles, setRoles] = useState([]);
  const [permisos, setPermisos] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedPermisos, setSelectedPermisos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadRoles();
    loadPermisos();
  }, []);

  useEffect(() => {
    if (selectedRole) {
      loadPermisosRol();
    }
  }, [selectedRole]);

  const loadRoles = async () => {
    setLoading(true);
    try {
      const response = await permisosApi.getRoles();
      if (response.data) {
        setRoles(Array.isArray(response.data) ? response.data : [response.data]);
      }
    } catch (error) {
      console.error('Error al cargar roles:', error);
      toast.error('Error al cargar roles');
    } finally {
      setLoading(false);
    }
  };

  const loadPermisos = async () => {
    try {
      const response = await permisosApi.getPermisos();
      if (response.data) {
        setPermisos(Array.isArray(response.data) ? response.data : [response.data]);
      }
    } catch (error) {
      console.error('Error al cargar permisos:', error);
      toast.error('Error al cargar permisos');
    }
  };

  const loadPermisosRol = async () => {
    if (!selectedRole) return;
    
    setLoading(true);
    try {
      const response = await permisosApi.getPermisosRol(selectedRole.id);
      if (response.data) {
        const permisoIds = Array.isArray(response.data) 
          ? response.data.map(p => p.id)
          : [response.data.id];
        setSelectedPermisos(permisoIds);
      } else {
        setSelectedPermisos([]);
      }
    } catch (error) {
      console.error('Error al cargar permisos del rol:', error);
      toast.error('Error al cargar permisos del rol');
      setSelectedPermisos([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = (e) => {
    const roleId = parseInt(e.target.value);
    const role = roles.find(r => r.id === roleId);
    setSelectedRole(role || null);
    setSelectedPermisos([]);
  };

  const handlePermisoToggle = (permisoId) => {
    setSelectedPermisos(prev => {
      if (prev.includes(permisoId)) {
        return prev.filter(id => id !== permisoId);
      } else {
        return [...prev, permisoId];
      }
    });
  };

  const handleSave = async () => {
    if (!selectedRole) {
      toast.error('Seleccione un rol');
      return;
    }

    setSaving(true);
    try {
      await permisosApi.asignarPermisosRol(selectedRole.id, selectedPermisos);
      toast.success('Permisos asignados correctamente');
      loadRoles(); // Recargar para actualizar contadores
    } catch (error) {
      console.error('Error al asignar permisos:', error);
      toast.error(error.response?.data?.detail || 'Error al asignar permisos');
    } finally {
      setSaving(false);
    }
  };

  // Agrupar permisos por categoría
  const permisosPorCategoria = permisos.reduce((acc, permiso) => {
    const categoria = permiso.nombre.split('.')[0] || 'otros';
    if (!acc[categoria]) {
      acc[categoria] = [];
    }
    acc[categoria].push(permiso);
    return acc;
  }, {});

  return (
    <Container fluid className="py-4">
      <ToastContainer />
      <Row>
        <Col>
          <Card>
            <Card.Header>
              <FaUserShield className="me-2" />
              <strong>Asignar Permisos a Roles</strong>
            </Card.Header>
            <Card.Body>
              <Row className="mb-4">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label><strong>Seleccionar Rol</strong></Form.Label>
                    <Form.Select
                      value={selectedRole?.id || ''}
                      onChange={handleRoleChange}
                      disabled={loading}
                    >
                      <option value="">-- Seleccione un rol --</option>
                      {roles.filter(r => r.activo).map(role => (
                        <option key={role.id} value={role.id}>
                          {role.nombre} {role.descripcion && `- ${role.descripcion}`}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              {selectedRole && (
                <>
                  <Alert variant="info" className="mb-4">
                    <strong>Rol seleccionado:</strong> {selectedRole.nombre}
                    {selectedRole.descripcion && (
                      <div className="mt-1">{selectedRole.descripcion}</div>
                    )}
                  </Alert>

                  {loading ? (
                    <div className="text-center py-5">
                      <Spinner animation="border" variant="primary" />
                    </div>
                  ) : (
                    <>
                      <div className="mb-3">
                        <strong>Permisos disponibles:</strong>
                        <Badge bg="primary" className="ms-2">
                          {selectedPermisos.length} de {permisos.length} seleccionados
                        </Badge>
                      </div>

                      {Object.entries(permisosPorCategoria).map(([categoria, permisosCat]) => (
                        <Card key={categoria} className="mb-3">
                          <Card.Header>
                            <strong>{categoria.toUpperCase()}</strong>
                          </Card.Header>
                          <Card.Body>
                            <Row>
                              {permisosCat.map(permiso => (
                                <Col md={6} lg={4} key={permiso.id} className="mb-2">
                                  <Form.Check
                                    type="checkbox"
                                    id={`permiso-${permiso.id}`}
                                    label={
                                      <div>
                                        <div><strong>{permiso.nombre}</strong></div>
                                        {permiso.descripcion && (
                                          <small className="text-muted">{permiso.descripcion}</small>
                                        )}
                                      </div>
                                    }
                                    checked={selectedPermisos.includes(permiso.id)}
                                    onChange={() => handlePermisoToggle(permiso.id)}
                                  />
                                </Col>
                              ))}
                            </Row>
                          </Card.Body>
                        </Card>
                      ))}

                      <div className="mt-4">
                        <Button
                          variant="primary"
                          onClick={handleSave}
                          disabled={saving || !selectedRole}
                        >
                          {saving ? (
                            <>
                              <Spinner animation="border" size="sm" className="me-2" />
                              Guardando...
                            </>
                          ) : (
                            <>
                              <FaCheck className="me-2" />
                              Guardar Permisos
                            </>
                          )}
                        </Button>
                        <Button
                          variant="secondary"
                          className="ms-2"
                          onClick={() => {
                            setSelectedRole(null);
                            setSelectedPermisos([]);
                          }}
                        >
                          <FaTimes className="me-2" />
                          Cancelar
                        </Button>
                      </div>
                    </>
                  )}
                </>
              )}

              {!selectedRole && (
                <Alert variant="warning">
                  Seleccione un rol para asignar permisos
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default PermisosRoles;
